// validate-form.js ── validation + serialization logic using formSchema + FORM_LIMITS
// Used by front-end to validate and serialize form data

/* This file contains the validation logic for the SRT form.
 * It validates the tyre fields, axle mass and height fields, and all other form fields.
 * It also provides functions to serialize the form data and enable live validation.
 * The validation is based on the form schema defined in srt-validate-form-limits.js.
 */

import { formLimitsSchema } from './srt-validate-form-limits.js';

/**
 * Re-validate all fields on form submit (calculate, refresh charts) from front-end
 */
export function validateAllFields(unitIndex = 0) {
   const { data, errors } = serializeAndValidateForm(unitIndex);
   const isValid = Object.keys(errors).length === 0;
   const isEmpty = Object.keys(data).length === 0;
   if (isEmpty) {
      console.log('Form appears to be empty.');
   }
   if (!isValid) {
      console.log('Form validation failed with errors:', errors);
   }
   return {
      valid: isValid,
      data,
   };
}

/**
 * Attach live validation events
 */
export function enableLiveValidation(unitIndex = 0) {
   const $unit = $(`[data-unit="${unitIndex}"]`).closest('.accordion-item');

   $('[data-field]', $unit).on('change blur', function () {
      const $el = $(this);
      const field = $el.data('field');
      let value;

      // Determine value type based on input element
      if ($el.is('select')) {
         value = $el.val(); // string, for selects
      } else if ($el.prop('type') === 'number') {
         value = parseFloat($el.val());
      } else if ($el.prop('type') === 'checkbox') {
         value = $el.is(':checked');
      } else {
         value = $el.val(); // fallback for text, hidden, etc.
      }

      const result = validateField(field, value, unitIndex);

      if (!result.valid) {
         $el.addClass('is-invalid');
         $el.closest('.input-group').next('.invalid-feedback').remove();
         $el.closest('.input-group').after(`<div class="invalid-feedback">${result.message}</div>`);
         $el.closest('.accordion-item').find('.accordion-button').addClass('invalid').attr('aria-invalid', 'true');
      } else {
         $el.removeClass('is-invalid');
         $el.closest('.input-group').next('.invalid-feedback').remove();
      }
   });
}

/**
 * Serialize + validate all fields in the given unit scope on form submit
 */
function serializeAndValidateForm(unitIndex = 0) {
   const data = {};
   const errors = {};
   let firstErrorEl = null;

   const $fields = $(`[data-unit="${unitIndex}"] [data-field]`);

   $fields.each(function () {
      const $el = $(this);
      const field = $el.data('field');
      let value;

      if ($el.is('select')) {
         value = $el.val();
      } else if ($el.prop('type') === 'number') {
         value = parseFloat($el.val());
      } else if ($el.prop('type') === 'checkbox') {
         value = $el.is(':checked');
      } else {
         value = $el.val();
      }

      const result = validateField(field, value, unitIndex);
      data[field] = value;

      const $group = $el.closest('.input-group');
      $group.next('.invalid-feedback').remove();

      if (!result.valid) {
         errors[field] = result;
         $el.addClass('is-invalid');

         // Insert feedback after input-group
         $group.after(`<div class="invalid-feedback">${result.message}</div>`);

         // Expand the accordion and mark the header
         const $accordion = $el.closest('.accordion-item');
         $accordion.find('.accordion-collapse').addClass('show');
         $accordion
            .find('.accordion-button')
            .addClass('invalid')
            .attr('aria-expanded', 'true')
            .attr('aria-invalid', 'true');

         if (!firstErrorEl) {
            firstErrorEl = $el[0];
         }
      } else {
         $el.removeClass('is-invalid');
      }
   });

   // Focus and scroll to the first invalid field
   if (firstErrorEl) {
      $('html, body').animate({ scrollTop: $(firstErrorEl).offset().top - 80 }, 300);
      $(firstErrorEl).focus();
   }
   if (Object.keys(errors).length > 0) {
      console.log('Validation errors:', errors);
   }
   return { data, errors };
}

/**
 * Validate a single input element using its field name and value during form serialization.
 */
function validateField(fieldName, value, unitIndex) {
   const def = formLimitsSchema.find((f) => f.name === fieldName);

   if (!def || def.readonly) {
      return {
         valid: true,
      };
   }

   if (def.ignore) {
      return { valid: true };
   }

   // Don't validate front axle fields for certain vehicle types
   // Get vehicle type for this unit only
   const vType = $(`[data-unit="${unitIndex}"] [data-field="vehicleType"]`).val();

   const skipFrontAxleFields = ['pig-trailer', 'jinker-trailer', 'semi-trailer', 'low-loader'];
   if (
      (fieldName.startsWith('f-') || String(fieldName).toLowerCase().includes('front')) &&
      skipFrontAxleFields.includes(vType)
   ) {
      return { valid: true };
   }
   // Handle the sloping / step required field cases that cant be empty when selected
   const styleDependentRequired = {
      sloping: new Set(['load-height-front', 'load-height-rear', 'load-bed-height-front', 'load-bed-height-rear']),
      step: new Set(['deck1-height', 'deck2-height', 'deck1-length', 'deck2-length']),
   };
   const selectedStyle = $('[data-field="deck-style"]').val();
   if (styleDependentRequired[selectedStyle]?.has(fieldName) && (value === '' || value === null || isNaN(value))) {
      return { valid: false, message: 'Required for selected deck style', level: 'error' };
   }

   // Handle the over-ride field cases that can be empty
   const overrideFields = new Set([
      'f-unsprung-mass',
      'f-unsprung-cg-height',
      'r-unsprung-mass',
      'r-unsprung-cg-height',
      'sprung-cg-height',
      'f-max-contact-width',
      'r-max-contact-width',
   ]);

   if (overrideFields.has(fieldName) && (value === '' || value === null || isNaN(value))) {
      return { valid: true };
   }

   // Check missing or invalid values in required fields
   if (def.required && (value === '' || value === null || value === undefined)) {
      return { valid: false, message: 'Required field', level: 'error' };
   }

   // validate select fields
   if (def.type === 'select' && def.options && !def.options.includes(value)) {
      return { valid: false, message: `Invalid option: ${value}`, level: 'error' };
   }

   // validate number fields min max step
   if (typeof value === 'number') {
      if (def.min !== undefined && value < def.min) {
         return { valid: false, message: `${value} - Must be ≥ ${def.min}`, level: 'error' };
      }
      if (def.max !== undefined && value > def.max) {
         return { valid: false, message: `${value} - Must be ≤ ${def.max}`, level: 'error' };
      }
      if (def.step !== undefined && (value - (def.min || 0)) % def.step !== 0) {
         return { valid: false, message: `${value} - Invalid step (use ${def.step})`, level: 'error' };
      }
   }

   return { valid: true };
}
